import { useState, FormEvent } from 'react';
import { CornerDownLeft, Bot, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Link } from 'react-router-dom';
import {
  ChatBubble,
  ChatBubbleAvatar,
  ChatBubbleMessage,
} from '@/components/ui/chat-bubble';
import { ChatMessageList } from '@/components/ui/chat-message-list';
import { ChatInput } from '@/components/ui/chat-input';

type Message = {
  role: 'user' | 'assistant';
  content: string;
};

const LegalAssistant = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: 'שלום! אני העוזר המשפטי הוירטואלי של משרד עורכי דין סמירנוב צולר. אני כאן כדי לעזור לך בנושאים של חדלות פירעון, הוצאה לפועל, מחיקת חובות ומקרקעין בישראל. במה אוכל לעזור לך היום?'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [hasAcceptedPrivacy, setHasAcceptedPrivacy] = useState(false);
  const { toast } = useToast();

  const sendMessage = async (userMessage: string) => {
    if (!userMessage.trim()) return;
    
    if (!hasAcceptedPrivacy) {
      toast({
        title: 'נדרשת הסכמה',
        description: 'עליך לאשר את תנאי השימוש ומדיניות הפרטיות לפני השימוש בעוזר המשפטי.',
        variant: 'destructive'
      });
      return;
    }

    const newUserMessage: Message = { role: 'user', content: userMessage };
    setMessages(prev => [...prev, newUserMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/legal-chat`;
      
      const response = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ 
          messages: [...messages, newUserMessage],
          userName: contactInfo.name || undefined,
          userPhone: contactInfo.phone || undefined,
          userEmail: contactInfo.email || undefined
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'שגיאה בתקשורת עם השרת');
      }

      if (!response.body) {
        throw new Error('לא התקבלה תשובה מהשרת');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';
      let buffer = '';

      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        
        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
          let line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            
            if (content) {
              assistantMessage += content;
              setMessages(prev => {
                const newMessages = [...prev];
                newMessages[newMessages.length - 1] = {
                  role: 'assistant',
                  content: assistantMessage
                };
                return newMessages;
              });
            }
          } catch (e) {
            buffer = line + '\n' + buffer;
            break;
          }
        }
      }

    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'שגיאה',
        description: error instanceof Error ? error.message : 'אירעה שגיאה בשליחת ההודעה',
        variant: 'destructive'
      });
      
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleContactSubmit = (e: FormEvent) => {
    e.preventDefault();
    
    if (!contactInfo.name || (!contactInfo.phone && !contactInfo.email)) {
      toast({
        title: 'נא למלא פרטים',
        description: 'יש למלא לפחות שם ואמצעי יצירת קשר אחד (טלפון או אימייל)',
        variant: 'destructive'
      });
      return;
    }

    toast({
      title: 'תודה!',
      description: 'הפרטים נשמרו. כעת אני יכול לספק לך מידע מותאם יותר.'
    });
    
    setShowContactForm(false);
  };

  return (
    <div className="min-h-screen bg-background md:pt-24 pt-20 pb-0 md:pb-8 px-0 md:px-4">
      <div className="w-full md:container md:mx-auto md:max-w-4xl h-full">
        <div className="mb-4 md:mb-6 text-center px-4 md:px-0">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">עוזר משפטי וירטואלי</h1>
          <p className="text-muted-foreground text-sm md:text-base">
            קבל מידע מקצועי על חדלות פירעון, הוצאה לפועל, מחיקת חובות ומקרקעין
          </p>
        </div>

        {!hasAcceptedPrivacy && (
          <Card className="mb-4 mx-2 md:mx-0 border-primary/50">
            <CardContent className="p-4 md:p-6">
              <h3 className="font-semibold mb-3">לפני שמתחילים - הסכמה לשימוש</h3>
              <p className="text-sm text-muted-foreground mb-4">
                העוזר המשפטי הווירטואלי משתמש בבינה מלאכותית. השיחות עשויות להישמר לצורך שיפור השירות.
                המידע שתספק אינו מהווה ייעוץ משפטי מחייב.
              </p>
              <div className="flex items-start gap-3 mb-4">
                <Checkbox
                  id="privacyAI"
                  checked={privacyConsent}
                  onCheckedChange={(checked) => setPrivacyConsent(checked as boolean)}
                  className="mt-1 shrink-0"
                />
                <label htmlFor="privacyAI" className="text-xs sm:text-sm cursor-pointer leading-relaxed">
                  קראתי והבנתי את{' '}
                  <Link to="/privacy" className="text-primary hover:underline" target="_blank">
                    מדיניות הפרטיות
                  </Link>
                  {' '}ו
                  <Link to="/terms" className="text-primary hover:underline" target="_blank">
                    תנאי השימוש
                  </Link>
                  . אני מסכים/ה ששיחותיי עם העוזר הווירטואלי עשויות להישמר ולהיות מועברות לספקי שירותי AI חיצוניים. *
                </label>
              </div>
              <Button 
                onClick={() => setHasAcceptedPrivacy(true)}
                disabled={!privacyConsent}
                className="w-full"
              >
                אישור והמשך
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className="mb-2 md:mb-4 mx-2 md:mx-0">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                רוצה ייעוץ אישי מעמיק יותר?
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowContactForm(!showContactForm)}
              >
                {showContactForm ? 'סגור טופס' : 'השאר פרטים'}
              </Button>
            </div>

            {showContactForm && (
              <form onSubmit={handleContactSubmit} className="space-y-4 border-t pt-4">
                <div>
                  <Label htmlFor="name">שם מלא *</Label>
                  <Input
                    id="name"
                    value={contactInfo.name}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="הזן את שמך המלא"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">טלפון</Label>
                  <Input
                    id="phone"
                    value={contactInfo.phone}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="05X-XXXXXXX"
                  />
                </div>
                <div>
                  <Label htmlFor="email">אימייל</Label>
                  <Input
                    id="email"
                    type="email"
                    value={contactInfo.email}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="example@mail.com"
                  />
                </div>
                <Button type="submit" className="w-full">
                  שמור פרטים
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <div className="h-[calc(100vh-280px)] md:h-[600px] border-t md:border bg-background md:rounded-lg flex flex-col mx-0 md:mx-0">
          <div className="flex-1 overflow-hidden">
            <ChatMessageList>
              {messages.map((message, index) => (
                <ChatBubble
                  key={index}
                  variant={message.role === 'user' ? 'sent' : 'received'}
                >
                  <ChatBubbleAvatar
                    className="h-8 w-8 shrink-0"
                    fallback={message.role === 'user' ? 'אתה' : 'בוט'}
                  />
                  <ChatBubbleMessage
                    variant={message.role === 'user' ? 'sent' : 'received'}
                  >
                    <p className="whitespace-pre-wrap text-right">{message.content}</p>
                  </ChatBubbleMessage>
                </ChatBubble>
              ))}

              {isLoading && (
                <ChatBubble variant="received">
                  <ChatBubbleAvatar
                    className="h-8 w-8 shrink-0"
                    fallback="בוט"
                  />
                  <ChatBubbleMessage isLoading />
                </ChatBubble>
              )}
            </ChatMessageList>
          </div>

          <div className="p-2 md:p-4 border-t">
            <form
              onSubmit={handleSubmit}
              className="relative rounded-lg border bg-background focus-within:ring-1 focus-within:ring-ring p-1"
            >
              <ChatInput
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="הקלד את שאלתך כאן..."
                className="min-h-16 md:min-h-12 resize-none rounded-lg bg-background border-0 p-4 shadow-none focus-visible:ring-0 text-base"
                disabled={isLoading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
              <div className="flex items-center p-2 md:p-3 pt-0 justify-end">
                <Button type="submit" size="default" className="gap-1.5 h-12 md:h-9 px-6" disabled={isLoading || !input.trim()}>
                  שלח הודעה
                  <CornerDownLeft className="size-4 md:size-3.5" />
                </Button>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-2 md:mt-4 text-center text-xs md:text-sm text-muted-foreground px-4 md:px-0 pb-4 md:pb-0">
          <p>
            * המידע המוצג כאן הוא למטרות הדרכה בלבד ואינו מהווה ייעוץ משפטי.
            לייעוץ אישי, אנא פנה למשרד.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LegalAssistant;
